/* ═══════════════════════════════════════════════════════
   NYC Suite: Schedule month grid helpers (pure, no React)

   The Jobs Schedule view (docs/holds/holds-plan.md, Frame E) draws a Monday-first
   month of six week rows. Everything here works on ISO YYYY-MM-DD strings and
   the entry shape lib/holds.js produces ({ key, owner, kind, label, date, ... }),
   so the view only renders. Months are 1 to 12.
   ═══════════════════════════════════════════════════════ */

const pad = (n) => String(n).padStart(2, "0");

export const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const fromISO = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const isoAddDays = (iso, n) => {
  const d = fromISO(iso);
  d.setDate(d.getDate() + n);
  return toISO(d);
};

// The year and month one step before or after (delta of -1 or 1, or any integer).
export function shiftMonth(year, month, delta) {
  const idx = year * 12 + (month - 1) + delta;
  return { year: Math.floor(idx / 12), month: ((idx % 12) + 12) % 12 + 1 };
}

// Six Monday-first weeks (42 ISO dates) that cover the month; the first row
// starts on the Monday on or before the 1st, so leading and trailing days belong
// to the neighbouring months. Six rows always, so the grid never changes height.
export function monthGrid(year, month) {
  const first = new Date(year, month - 1, 1);
  const dow = (first.getDay() + 6) % 7; // Monday 0 .. Sunday 6
  const start = new Date(year, month - 1, 1 - dow);
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(start);
      day.setDate(start.getDate() + w * 7 + d);
      week.push(toISO(day));
    }
    weeks.push(week);
  }
  return weeks;
}

export const isSameMonth = (iso, year, month) => iso.slice(0, 7) === `${year}-${pad(month)}`;

// "October 2026"
export const monthTitle = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });

// Entries grouped by date: Map<iso, entry[]>, each list in input order.
export function entriesByDate(entries) {
  const map = new Map();
  (Array.isArray(entries) ? entries : []).forEach((e) => {
    if (!e?.date) return;
    if (!map.has(e.date)) map.set(e.date, []);
    map.get(e.date).push(e);
  });
  return map;
}

// The bars for one week row: every owner (one job, one hold) that occupies two
// or more CONSECUTIVE days of that week draws one span per run. Columns are 0
// (Monday) to 6 (Sunday), inclusive on both ends. A run that continues into the
// next week is cut at Sunday and picked up again by the next row. Single days
// stay chips. Ordered by start column, then owner, so stacking is stable.
// `keys` carries EVERY entry key the span covers (a job's same-day setup and
// capture are two keys on one date), so the cells hide all of them. clientName
// and subject come from the owner's entries so a hold bar can print its title.
//   { owner, kind, label, clientName, subject, startCol, endCol, dates: [iso...], keys: [entryKey...] }
export function weekSpans(entries, week) {
  const days = Array.isArray(week) ? week : [];
  const col = new Map(days.map((iso, i) => [iso, i]));
  const byOwner = new Map();
  (Array.isArray(entries) ? entries : []).forEach((e) => {
    if (!e?.owner || !col.has(e.date)) return;
    if (!byOwner.has(e.owner)) byOwner.set(e.owner, { owner: e.owner, kind: e.kind, label: e.label, clientName: e.clientName || "", subject: e.subject || "", rows: new Map() });
    const o = byOwner.get(e.owner);
    if (!o.rows.has(e.date)) o.rows.set(e.date, []);
    o.rows.get(e.date).push(e.key);
  });
  const spans = [];
  for (const o of byOwner.values()) {
    const cols = [...o.rows.keys()].map((iso) => col.get(iso)).sort((a, b) => a - b);
    let run = [];
    const flush = () => {
      if (run.length >= 2) {
        const dates = run.map((c) => days[c]);
        spans.push({ owner: o.owner, kind: o.kind, label: o.label, clientName: o.clientName, subject: o.subject, startCol: run[0], endCol: run[run.length - 1], dates, keys: dates.flatMap((iso) => o.rows.get(iso)) });
      }
      run = [];
    };
    cols.forEach((c) => {
      if (run.length && c !== run[run.length - 1] + 1) flush();
      run.push(c);
    });
    flush();
  }
  return spans.sort((a, b) => a.startCol - b.startCol || (a.owner < b.owner ? -1 : a.owner > b.owner ? 1 : 0));
}

// The entry keys a week's spans already draw, so the cells skip those chips.
export const spannedKeys = (spans) => new Set((spans || []).flatMap((s) => s.keys));
